import React from 'react';
import PropTypes from 'prop-types';

import Browser from '@wetransfer/concorde-browser';

import { AppConfig } from '../../core-bundle/config/app-config';

import I18n from '@module/i18n';

import Actions from '@app/store/action';
import Store from '@app/store';

import Button from '@component/form/element/button';
import CountryField from '@component/form/element/countryfield';
import Form, { validationIndentifiers } from '@component/form';
import FormNotification from '@component/form/element/formnotification';
import TextArea from '@component/form/element/textarea';
import TextField from '@component/form/element/textfield';
import Radio from '@component/form/element/radio';
import Recaptcha from '@module/recaptcha';

export default class ContactSalesForm extends Form {
  static propTypes = {
    route: PropTypes.object,
  };

  formName = 'contactSales';

  /**
   * Binds all private methods
   * @param  {Object} props Component props
   */
  constructor(props) {
    super(props);

    this.state = {
      data: {
        oid: AppConfig.salesforce.OID,
        retURL: `${window.location.origin}/advertise/contact?submitted=true`,
        lead_source: 'Website',
        first_name: '',
        last_name: '',
        company: '',
        email: '',
        phone: '',
        currency: 'USD',
        country_code: Store.getState().session.region.country,
        description: '',
        [AppConfig.salesforce.budgetFieldID]: '', // Add custom form field
      },
    };

    this.submit = this.submit.bind(this);
    this.validate = this.validate.bind(this);
    this.getErrorMessage = this.getErrorMessage.bind(this);
  }

  componentDidMount() {
    Recaptcha.load();
  }

  // Returns the first error that comes up for a field. Remote errors have priority over local errors.
  getError(name) {
    // Local errors
    if (!document.activeElement) {
      return false;
    }
    if (
      document.activeElement.name === name &&
      this.getLocalError(name) &&
      this.state.data[name]
    ) {
      return true;
    }
    if (
      document.activeElement.name !== name &&
      this.getLocalError(name) &&
      this.state.data[name]
    ) {
      return this.getLocalError(name);
    }

    return false;
  }

  validate(key) {
    if (this.getError(key)) {
      return validationIndentifiers.NOT_VALID;
    }

    return validationIndentifiers.VALID;
  }

  // Checks for local and remote errors and whether a submit is still pending, is used to (de-)activate submitButton.
  formValidation() {
    for (const key in this.state.data) {
      if (!this.state.data[key]) {
        return false;
      }
      if (this.validate(key) === validationIndentifiers.NOT_VALID) {
        return false;
      }
    }

    return true;
  }

  // Dispatches the request to create an account.
  async submit(event) {
    event.preventDefault();
    if (!this.formValidation()) return;

    try {
      await Recaptcha.execute();
    } catch {
      Recaptcha.reset();
      return;
    }

    const { data } = this.state;

    Store.dispatch(Actions.Route.setFormState(false));
    Store.dispatch(Actions.Advertise.submit(data));
  }

  renderCurrencyRadios() {
    const currencies = ['USD', 'EUR', 'GBP'];
    return currencies.map((currency) => {
      return (
        <Radio
          key={`currency_${currency}`}
          label={currency}
          name={`currency_${currency}`}
          value={currency}
          checked={this.state.data.currency === currency}
          onChange={(e) => this.updateValue('currency', e.target.value)}
        />
      );
    });
  }

  render() {
    const { route } = Store.getState();
    const active = this.formValidation();
    const submitText = I18n.t('page.advertise.contact.form.button');
    const { budgetFieldID } = AppConfig.salesforce;

    if (route.query.submitted) {
      return (
        <div className="contact-sales-form contact-sales-form--is-thankful">
          <h1>{I18n.t('page.advertise.contact.title_success')}</h1>
          <p>{I18n.t('page.advertise.contact.description_success_032019')}</p>
        </div>
      );
    }

    return (
      <div className="contact-sales-form">
        <h1>{I18n.t('page.advertise.contact.title_1905')}</h1>
        {!Browser.isMobile ? (
          <p>{I18n.t('page.advertise.contact.description')}</p>
        ) : null}
        <form onSubmit={this.submit}>
          <TextField
            label={I18n.t('page.advertise.contact.form.first_name')}
            value={this.state.data.first_name}
            name="first_name"
            onChange={(e) => this.updateValue('first_name', e.target.value)}
            valid={this.validate('first_name')}
          />
          <FormNotification
            message={this.getErrorMessage('first_name')}
            type="error"
          />

          <TextField
            label={I18n.t('page.advertise.contact.form.last_name')}
            value={this.state.data.last_name}
            name="last_name"
            onChange={(e) => this.updateValue('last_name', e.target.value)}
            valid={this.validate('last_name')}
          />
          <FormNotification
            message={this.getErrorMessage('last_name')}
            type="error"
          />

          <TextField
            label={I18n.t('page.advertise.contact.form.company')}
            value={this.state.data.company}
            name="company"
            onChange={(e) => this.updateValue('company', e.target.value)}
            valid={this.validate('company')}
          />
          <FormNotification
            message={this.getErrorMessage('company')}
            type="error"
          />

          <TextField
            type="email"
            label={I18n.t('page.advertise.contact.form.email')}
            value={this.state.data.email}
            name="email"
            onChange={(e) => this.updateValue('email', e.target.value)}
            valid={this.validate('email')}
          />
          <FormNotification
            message={this.getErrorMessage('email')}
            type="error"
          />

          <CountryField
            value={this.state.data.country_code}
            defaultValue={Store.getState().session.region.country}
            errorMessage={this.getErrorMessage('country_code')}
            updateValue={this.updateValue}
          />

          <TextField
            label={I18n.t('page.advertise.contact.form.telephone')}
            value={this.state.data.phone}
            name="phone"
            onChange={(e) => this.updateValue('phone', e.target.value)}
            valid={this.validate('phone')}
          />
          <FormNotification
            message={this.getErrorMessage('phone')}
            type="error"
          />

          <TextField
            label={I18n.t('page.advertise.contact.form.budget')}
            value={this.state.data[budgetFieldID]}
            name={budgetFieldID}
            onChange={(e) => {
              this.updateValue(budgetFieldID, e.target.value);
            }}
            valid={this.validate(budgetFieldID)}
          />
          <FormNotification
            message={this.getErrorMessage(budgetFieldID)}
            type="error"
          />

          <div className="contact-sales-form__currency" role="radiogroup">
            {this.renderCurrencyRadios()}
          </div>

          <TextArea
            label={I18n.t('page.advertise.contact.form.description')}
            defaultValue={this.state.data.description}
            name="description"
            onChange={(e) => this.updateValue('description', e.target.value)}
            valid={this.validate('description')}
          />
          <FormNotification
            message={this.getErrorMessage('description')}
            type="error"
          />

          <Button
            onClick={this.submit}
            active={active}
            text={submitText}
            submit={true}
          />
        </form>
      </div>
    );
  }
}
